import type { ReactNode } from 'react';
import type { Descendant, Editor, NodeEntry } from 'slate';
import { Node, Text, Element } from 'slate';
import { safeJsonParse } from '@halo/utils';
import { jsx } from 'slate-hyperscript';
import type { CustomDescendant } from '../types';

export interface PayloadDataEntity {
  type: string;
  offset: number;
  length: number;
  [key: string]: any;
}

export type PayloadData = {
  content: string;
  entities: PayloadDataEntity[];
  [key: string]: any;
};

export interface SerializePayloadCallback {
  (value: { part?: string; entity?: Omit<PayloadDataEntity, 'offset' | 'length'> }): void;
}

export interface SerializePayloadHandlerOptions {
  editor: Editor;
  entry: NodeEntry;
  context: Record<string, any>;
  payload: PayloadData;
  callback: SerializePayloadCallback;
}

export interface DeserializePayloadStringHandlerOptions<
  T extends Record<string, any> = Record<string, any>,
> {
  key: string;
  text: string;
  payload: PayloadData;
  props: T;
}

export interface DeserializePayloadHandlerOptions<
  T extends Record<string, any> = Record<string, any>,
> {
  type: string;
  key: string;
  text: string;
  entity: PayloadDataEntity;
  props: T;
}

export interface DeserializePayloadHandlerResult {
  children: ReactNode;
}

export interface DeserializePayloadValueHandlerOptions {
  type: string;
  text: string;
  entity: PayloadDataEntity;
}

export interface DeserializePayloadValueHandlerResult {
  descendant: CustomDescendant;
}

export abstract class SerializePayloadHandler {
  abstract serialize(options: SerializePayloadHandlerOptions): true | void;
  /**
   * Deserialize plain text
   */
  deserializeString?(
    options: DeserializePayloadStringHandlerOptions
  ): void | DeserializePayloadHandlerResult;
  /**
   * Deserialize structured content
   */
  deserialize?(options: DeserializePayloadHandlerOptions): void | DeserializePayloadHandlerResult;
  /**
   * Deserialize structured content to slate nodes
   */
  deserializeValue?(
    options: DeserializePayloadValueHandlerOptions
  ): void | DeserializePayloadValueHandlerResult;
}

type ExtractHandlerProps<
  M extends keyof SerializePayloadHandler,
  T extends readonly SerializePayloadHandler[],
> = {
  readonly [K in keyof T]: T[K] extends SerializePayloadHandler
    ? T[K][M] extends (options: DeserializePayloadHandlerOptions<infer P>) => any
      ? P
      : Record<string, any>
    : Record<string, any>;
};

export interface SerializePayloadResult {
  payload: string;
  context: Record<string, any>;
  value: Descendant[];
}

export class SerializePayload<const H extends readonly SerializePayloadHandler[]> {
  private handlers: H;
  constructor(handlers: H) {
    this.handlers = handlers;
  }
  eachHandlers<M extends keyof SerializePayloadHandler>(
    method: M,
    args:
      | Parameters<Exclude<SerializePayloadHandler[M], undefined>>
      | ((index: number) => Parameters<Exclude<SerializePayloadHandler[M], undefined>>)
  ): ReturnType<Exclude<SerializePayloadHandler[M], undefined>> | void {
    const isCallback = typeof args === 'function';

    for (let i = 0; i < this.handlers.length; i++) {
      const handler = this.handlers[i];
      const result = handler[method]?.(...((isCallback ? args(i) : args) as [any]));
      if (result) {
        return result as any;
      }
    }
  }
  serialize(editor: Editor): SerializePayloadResult {
    const context: Record<string, any> = {};
    const payload: PayloadData = {
      content: '',
      entities: [],
    };

    const callback: SerializePayloadCallback = ({ part, entity }) => {
      if (part) {
        if (entity) {
          payload.entities.push({
            type: '',
            offset: payload.content.length,
            length: part.length,
            ...entity,
          });
        }
        payload.content += part;
      }
    };

    const gen = Node.nodes(editor);

    for (const entry of gen) {
      const [node] = entry;

      const isText = Text.isText(node);
      const isElement = Element.isElement(node);
      const isVoid = isElement && editor.isVoid(node);
      const isBlock = isElement && editor.isBlock(node);

      if (isText ? node.text : isElement ? isVoid : false) {
        const has = this.eachHandlers('serialize', [
          {
            editor,
            entry,
            context,
            payload,
            callback,
          },
        ]);

        if (!has) {
          callback({
            part: Node.string(node),
          });
        }
      }

      if (isBlock && payload.content) {
        payload.content += '\n';
      }
    }

    return {
      payload: payload.content ? JSON.stringify(payload) : '',
      context,
      value: editor.children,
    };
  }
  deserializePayloadData(
    payloadData: PayloadData,
    formats: {
      string: (content: string, info: { currentIndex: number; offset: number }) => void;
      entity: (result: { content: string; entity: PayloadDataEntity }) => void;
    }
  ) {
    let currentIndex = 0;
    const content = payloadData.content;

    payloadData.entities
      ?.sort((a, b) => a.offset - b.offset)
      .forEach((entity) => {
        const { offset, length } = entity;
        const entityEnd = offset + length;

        if (currentIndex < offset) {
          formats.string(content.substring(currentIndex, offset), { currentIndex, offset });
        }

        const entityText = content.substring(offset, entityEnd);

        if (entityText) {
          formats.entity({ content: entityText, entity });
        }

        currentIndex = entityEnd;
      });

    if (content?.length && currentIndex < content.length) {
      formats.string(content.substring(currentIndex), { currentIndex, offset: content.length });
    }
  }
  deserialize(
    payload: Record<string, any> | string | undefined,
    deserializePropsList: ExtractHandlerProps<'deserialize', H>,
    deserializeStringPropsList: ExtractHandlerProps<'deserializeString', H>
  ): ReactNode {
    let payloadData: PayloadData | undefined = undefined;
    let payloadDisplay: ReactNode = undefined;

    switch (typeof payload) {
      case 'string':
        payloadData = safeJsonParse(payload, {
          content: payload,
          entities: [],
        });
        payloadDisplay = payload;
        break;
      case 'object':
        payloadData = payload as PayloadData;
        payloadDisplay = payload.content || JSON.stringify(payload);
        break;
      default:
        payloadData = undefined;
        payloadDisplay = 'Unknown content';
        break;
    }

    if (payloadData) {
      const resultNodes: ReactNode[] = [];

      this.deserializePayloadData(payloadData, {
        string: (content, { currentIndex, offset }) => {
          let children: ReactNode = content;

          const result = this.eachHandlers('deserializeString', (i) => [
            {
              key: `${currentIndex}-${offset}`,
              payload: payloadData,
              text: content,
              props: deserializeStringPropsList[i],
            },
          ]);

          if (result) {
            children = result.children;
          }

          resultNodes.push(children);
        },
        entity: ({ content, entity }) => {
          const { offset, length, type } = entity;

          let children: ReactNode = content;

          const result = this.eachHandlers('deserialize', (i) => [
            {
              type,
              entity,
              key: `${offset}-${length}`,
              text: content,
              props: deserializePropsList[i],
            },
          ]);

          if (result) {
            children = result.children;
          }

          resultNodes.push(children);
        },
      });

      payloadDisplay = resultNodes;
    }

    return payloadDisplay;
  }
  deserializeContent(payload: Record<string, any> | string | undefined) {
    if (typeof payload === 'string') {
      payload = safeJsonParse(payload, {});
    }
    if (payload && payload.content && payload.entities) {
      return payload.content;
    }
    return JSON.stringify(payload);
  }
  deserializeValue(payload: Record<string, any> | string | undefined) {
    let payloadData: PayloadData | undefined = undefined;
    let descendants: CustomDescendant[];

    switch (typeof payload) {
      case 'string':
        payloadData = safeJsonParse(payload, {
          content: payload,
          entities: [],
        });
        descendants = [jsx('element', payload)];
        break;
      case 'object':
        payloadData = payload as PayloadData;
        descendants = [jsx('element', payload.content || JSON.stringify(payload))];
        break;
      default:
        payloadData = undefined;
        descendants = [jsx('element', 'Unknown content')];
        break;
    }

    if (payloadData && payloadData.entities.length) {
      const children: CustomDescendant[] = [];

      this.deserializePayloadData(payloadData, {
        string: (content) => {
          children.push(jsx('text', content));
        },
        entity: ({ content, entity }) => {
          const { type } = entity;

          let descendant: CustomDescendant = jsx('text', content);

          const result = this.eachHandlers('deserializeValue', [
            {
              type,
              entity,
              text: content,
            },
          ]);

          if (result) {
            descendant = result.descendant;
          }

          children.push(descendant);
        },
      });

      descendants = jsx('fragment', [jsx('text'), ...children, jsx('text')]);
    }

    return descendants;
  }
}
